import React from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { colors } from "style";
import Overlay from "components/Overlay";
import { Tag, TAG_SIZE } from "components/Tags";
import { PopoverCard } from "components/ActivityLog";
import ListTable from "components/ListTable";
import Icon, { ICON_SIZE, ICON_TYPE } from "components/Icon";

/**
 * InterviewAnswerLocation
 *
 * @param {String}  city
 * @param {String}  cityConfidence
 * @param {String}  region
 * @param {String}  regionConfidence
 * @param {String}  country
 * @param {String}  countryConfidence
 * @param {Boolean} anonymizationDetected
 * @param {Object}  anonymizationData
 */
const InterviewAnswerLocation = ({
  city,
  cityConfidence,
  region,
  regionConfidence,
  country,
  countryConfidence,
  anonymizationDetected,
  anonymizationData,
}) => {
  if (!city && !region && !country) return null;

  return (
    <div css={styles.meta_row}>
      <Overlay
        overlay={
          <PopoverCard>
            <ListTable
              data={TABLE_DATA({
                city,
                cityConfidence,
                region,
                regionConfidence,
                country,
                countryConfidence,
                anonymizationDetected,
                anonymizationData,
              })}
            />
          </PopoverCard>
        }
        trigger={
          <Tag size={TAG_SIZE.medium} css={[styles.location_tag, anonymizationDetected && styles.location_tag_warning]}>
            <Icon type={ICON_TYPE.pin} size={ICON_SIZE.medium} />
            {formatLocation(city, region, country)}
          </Tag>
        }
      />
    </div>
  );
};

const formatLocation = (city, region, country) => {
  return [city, region, country].filter(Boolean).join(", ");
};

const formatValueWithConfidence = (value, confidence) => {
  if (!value) return "N/A";
  if (confidence == null) return value;

  if (confidence < 50) {
    return `${value} (${confidence}% - low confidence)`;
  }

  return `${value} (${confidence}%)`;
};

const TABLE_DATA = (data) => [
  {
    label: "City",
    value: formatValueWithConfidence(data.city, data.cityConfidence),
  },
  {
    label: "Region",
    value: formatValueWithConfidence(data.region, data.regionConfidence),
  },
  {
    label: "Country",
    value: formatValueWithConfidence(data.country, data.countryConfidence),
  },
  ...(data.anonymizationDetected
    ? [
        {
          label: "Anonymization Detected",
          value: "Yes",
        },
        {
          label: "Provider",
          value: data.anonymizationData?.providerName,
        },
      ]
    : []),
];

const styles = {
  meta_row: css`
    margin-top: 0.75rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  `,
  location_tag: css`
    background: ${colors.grayAnatomyLight5};
    color: ${colors.grayAnatomyDark};
    cursor: pointer;
  `,
  location_tag_warning: css`
    background: rgba(217, 119, 6, 0.12);
    color: #d97706;
    border: 1px solid rgba(217, 119, 6, 0.3);
  `,
};

InterviewAnswerLocation.propTypes = {
  city: PropTypes.string,
  region: PropTypes.string,
  country: PropTypes.string,
  cityConfidence: PropTypes.number,
  regionConfidence: PropTypes.number,
  countryConfidence: PropTypes.number,
  anonymizationData: PropTypes.shape({
    providerName: PropTypes.string,
  }),
  anonymizationDetected: PropTypes.bool,
};

export default InterviewAnswerLocation;
